/**
 * Two things the browser does by itself that OPButtons now stops (2026-11-03, after the back button; operator: "2 yes!
 * good shout, maybe a prompt indicated where to drop it", "3 ok").
 *
 * 1. A FILE DROPPED OUTSIDE A DROP ZONE. Chrome's own answer to a drop nothing took is to open the file in place of
 *    the page: OPButtons gone, the file on screen. Every file drag is now answered at the window, after the drop
 *    zones (bubble phase, so a zone that took it has called preventDefault): one that no zone took is refused (the
 *    no-drop cursor, and no drop), and `dropCatch()` says so, so `DropCatch.tsx` can show where audio goes, itself a
 *    drop zone into the Audio library. Only drags that carry FILES: a drag inside the app (a lane, an audio row) is
 *    left alone.
 *
 * 2. PAGE ZOOM on a desktop: Ctrl + the wheel, a trackpad's pinch (Chrome sends it as Ctrl + wheel) and Ctrl + plus,
 *    minus or zero zoomed the whole app, and Chrome keeps that zoom for the site. The canvas fits itself and the
 *    timeline editor uses Ctrl + the wheel for lane height (its own handler still runs: this only stops the browser).
 *    The iPad's pinch is already stopped (App.tsx, DEC-142's touch-action and the gesture events).
 */

import { createSignal } from 'solid-js';

/** A file drag over the app that no drop zone takes: where it is (for the panel), and whether it looks like audio
 *  (null when the browser does not say). `over` = on the panel itself. Null when no such drag. */
export interface DropCatch { audio: boolean | null; over: boolean }
const [catchSig, setCatch] = createSignal<DropCatch | null>(null);
export const dropCatch = catchSig;

const hasFiles = (e: DragEvent) => !!e.dataTransfer && Array.from(e.dataTransfer.types).includes('Files');
/** Audio by the MIME types the browser shows during a drag (Chrome shows them; null when it does not say). */
function looksAudio(e: DragEvent): boolean | null {
  const items = Array.from(e.dataTransfer?.items ?? []).filter((i) => i.kind === 'file');
  if (!items.length || items.every((i) => !i.type)) return null;
  return items.some((i) => i.type.startsWith('audio/'));
}

type GuardWindow = Window & { __opbBrowserGuards?: true };

/** Install once (App). */
export function installBrowserGuards(): void {
  const w = window as GuardWindow;
  if (w.__opbBrowserGuards) return;
  w.__opbBrowserGuards = true;

  // ── 1. stray file drops ──
  let clear: ReturnType<typeof setTimeout> | undefined;
  const later = () => { clearTimeout(clear); clear = setTimeout(() => setCatch(null), 250); };
  window.addEventListener('dragover', (e) => {
    if (!hasFiles(e)) return;
    const onPanel = !!(e.target as Element | null)?.closest?.('[data-drop-catch]');
    if (onPanel) {
      // The panel is a drop zone: copy.
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
      setCatch({ audio: looksAudio(e), over: true });
      later();
      return;
    }
    if (e.defaultPrevented) { setCatch(null); return; } // a drop zone took it
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'none';
    setCatch({ audio: looksAudio(e), over: false });
    later();
  });
  // Belt and braces: a drop that reached the window untaken never opens the file.
  window.addEventListener('drop', (e) => {
    setCatch(null);
    if (hasFiles(e) && !e.defaultPrevented) e.preventDefault();
  });
  window.addEventListener('dragend', () => setCatch(null));

  // ── 2. page zoom ──
  window.addEventListener('wheel', (e) => { if (e.ctrlKey) e.preventDefault(); }, { passive: false, capture: true });
  window.addEventListener('keydown', (e) => {
    if ((e.ctrlKey || e.metaKey) && !e.altKey && ['+', '=', '-', '_', '0'].includes(e.key)) e.preventDefault();
  }, true);
}
